/**
 * Bob's song. The hound sings while it looks for you.
 *
 * ---------------------------------------------------------------------------
 * THE ONE BINARY ASSET IN THE REPOSITORY — DO NOT "CLEAN THIS UP"
 *
 * `public/audio/bobs-song.m4a` is a real recording of a real person singing,
 * and it is the single deliberate exception to the "no asset files at all"
 * rule in CLAUDE.md. It is not a placeholder, it is not leftover scaffolding,
 * and it is not something that was going to be replaced with synthesis later.
 * It is the point. Deleting it to bring the repo back in line with the rule
 * removes the best thing in the build.
 *
 * It is the author's own recording, so there is nothing to clear and no
 * licence to track.
 * ---------------------------------------------------------------------------
 *
 * ## Why this is a good idea and not just a joke
 *
 * The hound is blind. It has `sightRange: 0` and knows the world purely through
 * the noise the player makes, which is why the counterplay is to stop moving.
 * The whole encounter is an argument about sound: it is listening for you, and
 * you cannot see it.
 *
 * So give it a voice that never stops. A hound that sings is the exact mirror
 * of the player's own problem — it broadcasts its position continuously, the
 * way the player does when they walk, and it stops broadcasting the moment it
 * has you. The song is not decoration bolted onto the entity; it is the
 * entity's sonar pointed back at itself, and it is readable: a direction to
 * walk away from, coming from something singing a child's song about nothing.
 *
 * ## When it plays, and why it is every hound rather than a rare surprise
 *
 * Every living hound sings. A rare occurrence is usually scarier than a
 * constant one and that instinct is right in general, but it is wrong here, for
 * three reasons:
 *
 *  1. The rarity already exists one level up. Hounds are *events* — the
 *     director places them minutes apart, if at all. Gating the song on top of
 *     an already-rare entity means a player can run for an hour and never hear
 *     it, which is the wrong trade for a song somebody wrote for this game.
 *  2. A tell only works if it is reliable. Consistent means readable, and
 *     readable means it is a mechanic. An intermittent singing hound is not
 *     scarier, it is just a sound the player cannot learn anything from.
 *  3. The fear is not in the song arriving. It is in the song *leaving*. See
 *     below — that transition is the whole effect, and it needs the song to
 *     have been reliably there for the absence to mean anything.
 *
 * ## The growl always wins
 *
 * The hound's growl is the player's warning: `growlLead` of 1.6 s before it
 * commits, 3.6 s for the first of a run, repeating on `growlPulse`. That
 * warning is verified and tuned and nothing in this file is allowed to bury it.
 * Three separate mechanisms keep it clear, in order of how much they matter:
 *
 *  - **Spectral.** Measured on the recording itself, its single loudest band is
 *    150-300 Hz at -17.9 dB — which is exactly where the growl's main formant
 *    (165-210 Hz), its sub-saw (54-66 Hz) and the start of its commit sweep
 *    (230 Hz upward) live. That is a direct collision, so the song is
 *    high-passed at `HIGHPASS_HZ` and the growl gets that whole region back.
 *    Measured through the actual filter, that is 7.1 dB of clearance in
 *    150-300 Hz, 8.9 dB across the main formant and 11.9 dB below 150 Hz, for
 *    5.6 dB of overall level. This one is static: it holds even if every gain
 *    below is wrong, and it is the mechanism to keep if any is dropped.
 *    It also happens to be the correct sound — thin and small and coming out of
 *    an animal — and it keeps a hot phone recording out of the low end of a mix
 *    that is otherwise very quiet.
 *  - **Mode.** `MODE_DUCK` drops the song hard the moment the hound commits, so
 *    the wind-up growls play into a hole rather than over a song.
 *  - **Transient.** `duckUnderVocal()` is called from the engine on every growl
 *    and scream however it was triggered, which also covers the idle grumble
 *    while wandering and — the one that matters most — the deflating grumble a
 *    hound makes when it gives up on a player who stood still. That teaches the
 *    counterplay by confirming it worked, and it must never be sung over.
 *
 * The mode duck is deliberately a duck and not a cut. The song dropping away
 * into a growl is a second, redundant channel carrying the same warning the
 * game already teaches, arriving on the same frame; it reinforces the growl
 * rather than replacing it, and a player who learns the silence has learned the
 * growl. Cutting to true silence would invert that — it would become a cue in
 * its own right that a player could rely on *instead* of the growl, and the
 * growl is the thing that must be listened for.
 *
 * Nothing here reads or writes entity state. This layer is pure output: it
 * watches `ctx.entities` and makes a noise. It cannot change how the hound
 * finds anyone, and crouching loses it exactly as before.
 *
 *   <audio> ─> src ─> hp ─> level ─> lp ─> pan ─┬─> graph.dry
 *                                               └─> send ─> graph.reverbIn
 *
 * The chain is the same shape as a pooled voice in `voices.ts` and a peer in
 * `voicepeer.ts`, and the placement is the game's own `evalSpatial` — the same
 * function that puts a thud four rooms away. So the song is panned, rolled off,
 * darkened with distance and sent to the same convolver as everything else: a
 * hound singing in the Poolrooms is in the Poolrooms.
 *
 * Everything fails silently. A missing file, a decode the browser refuses, a
 * platform that will not give us a media element source — all of them end with
 * `out === null` and a hound that hunts in silence, exactly as it used to.
 */

import { clamp, clamp01 } from '../core/math';
import type { EntityMode, EntityState } from '../core/types';
import type { AudioGraph } from './graph';
import { evalSpatial, makeSpatial, MAX_AUDIBLE, type Spatial } from './spatial';

/** Relative to the page, so it survives being served from a subdirectory. */
const SRC = 'audio/bobs-song.m4a';

/**
 * Corner of the high-pass that keeps the growl's band clear, in Hz.
 *
 * Two-pole at Q 0.707, so the rejection across the growl's own bands is -30.9 dB
 * at 54 Hz, -11.8 dB at 165 Hz, -8.1 dB at 210 Hz and -6.8 dB at 230 Hz, while
 * costing the song only -1.5 dB at 400 Hz and -0.4 dB at 560 Hz. Run over the
 * real file it takes 5.6 dB off the total, so this is a substantial cut and not
 * a token one — which is why `SONG_LEVEL` below looks higher than it sounds.
 */
const HIGHPASS_HZ = 320;

/**
 * Level of the song before distance, applied after the high-pass.
 *
 * Anchored to two numbers already in this mix rather than picked by ear. The
 * music layer plays a *foreign audio file of unknown loudness* at
 * `LOCAL_LEVEL` = 0.099 (`music.ts`), which is the closest existing analogue;
 * an entity vocal sits at 0.5-1.0. The song belongs above the first, because it
 * is meant to be noticed and located rather than overheard, and below the
 * second, because those are transients and this is continuous — the recording
 * is a dense -15.6 LUFS with only 4.2 LU of range, so it is far more *present*
 * than a one-shot at the same peak gain.
 *
 * The high-pass costs a measured 5.6 dB, so the effective level against the raw
 * file is about 0.20 — 12.6 dB under a wind-up growl's 0.85 at the same
 * distance, before the growl's spectral clearance is counted at all.
 *
 * This is the number most likely to want moving after somebody actually listens
 * to it. Move this one; leave the ducks alone.
 */
const SONG_LEVEL = 0.38;

/** Base reverb send, scaled by distance the way every other world sound is. */
const SEND = 0.28;

/**
 * How much of the song survives in each of the hound's modes.
 *
 * `stalk` is the wind-up — the warning — and is the important row: the song
 * drops to a fifth on the same frame the first growl goes out. `chase` and
 * `attack` are lower still, because by then the scream, the footfalls on
 * `stepInterval` and the player's own panic are the entire mix and a song in
 * there is just mud.
 *
 * A hound that is `dormant` has not really arrived yet, and a `dead` one has
 * stopped being anything.
 */
const MODE_DUCK: Partial<Record<EntityMode, number>> = {
  dormant: 0,
  wander: 1,
  patrol: 1,
  investigate: 1,
  // These were 0.2 / 0.1 / 0.06, which turned out to be the whole song's worst
  // bug: a player only *sees* a hound once it is coming for them, so the one
  // moment anybody meets Bob was the one moment his song was inaudible. It was
  // reported exactly that way — "he saw Bob come but it didn't play the song".
  //
  // The growl is still protected, twice over and both times better than this
  // was doing it: `duckUnderVocal()` pulls the song down for the length of each
  // growl, which is a transient and is when the collision actually happens, and
  // the 320 Hz high-pass leaves 7.1 dB of clearance in the growl's own band at
  // all times. A standing duck across the entire chase was a third defence
  // against a problem the first two had already solved.
  stalk: 0.85,
  chase: 0.7,
  attack: 0.5,
  flee: 0.5,
  dead: 0,
};

/** Anything not in `MODE_DUCK`. Audible, since a silent hound is the surprise. */
const MODE_DUCK_DEFAULT = 1;

/** Where a growl or a scream pins the song. */
const DIP_TO = 0.22;

/**
 * Seconds the song is held at `DIP_TO` before it starts climbing back.
 *
 * This is the growl's own length — `'hound-growl'` is a 1.8 s one-shot in
 * `sfx.ts` — and it has to be, or the song is back up before the sound it was
 * ducking for has finished. A plain exponential recovery is not enough here and
 * the numbers say why: the wind-up fires a growl every `growlPulse` = 0.95 s,
 * so anything that recovers inside a second is fully back between pulses. The
 * hold re-arms on every growl, which means one grumble punches a hole for its
 * own duration and a whole wind-up train keeps the song floored from the first
 * growl to the last without needing to know that a wind-up is happening.
 */
const DIP_HOLD = 1.8;

/**
 * Seconds to climb back to full once the hold expires. Matches the recovery
 * `duckMusic` already uses for an entity vocal in `engine.ts`, because it is
 * the same judgement about the same class of sound.
 */
const DIP_RECOVER = 2.6;

/**
 * Metres of slack past the audible limit before the element is paused.
 *
 * Without it a hound pacing the limit starts and stops the media element
 * several times a second, which some engines answer with an audible click and
 * all of them answer with more work than a gain of zero costs.
 */
const RANGE_SLACK = 4;

/**
 * Metres a rival hound must be closer by before the song moves to it. Two
 * hounds are rare and two copies of the same song out of phase would be a mess,
 * so the nearest one sings — with enough hysteresis that a pair running abreast
 * cannot flip the source back and forth.
 */
const RETARGET_MARGIN = 4;

/** Fast down, slow up: get out of the way instantly, come back gently. */
const TAU_DOWN = 0.05;
const TAU_UP = 0.35;

export class HoundSong {
  private readonly g: AudioGraph;
  private readonly scratch: Spatial = makeSpatial();

  /** Built on first sight of a hound, so a run without one allocates nothing. */
  private el: HTMLAudioElement | null = null;
  private src: MediaElementAudioSourceNode | null = null;
  private hp: BiquadFilterNode | null = null;
  private level: GainNode | null = null;
  private lp: BiquadFilterNode | null = null;
  private panner: StereoPannerNode | null = null;
  private send: GainNode | null = null;

  /** Set once the media path has proven unavailable, so we stop retrying. */
  private failed = false;
  private started = false;

  /** Entity id of the hound currently singing, or null. */
  private targetId: number | null = null;

  private dipValue = 1;
  private dipHold = 0;
  private lastLevel = 0;

  constructor(g: AudioGraph) {
    this.g = g;
  }

  /**
   * Pin the song down under a growl or a scream. Called by the engine for every
   * one of them, whoever played it.
   *
   * Idempotent within a hold: re-arming is what makes a pulse train read as one
   * continuous hole rather than as a series of separate ones.
   */
  duckUnderVocal(): void {
    this.dipValue = DIP_TO;
    this.dipHold = DIP_HOLD;
  }

  /**
   * Per-frame. `active` is false while the game is paused or the player is
   * dead — the world stops making noise then, and a dog singing over the pause
   * menu would be the one sound still running.
   */
  update(dt: number, entities: readonly EntityState[], active: boolean): void {
    // A suspended context has a frozen clock; every ramp below would land at
    // once when it resumes. Sit it out.
    if (this.g.ctx.state !== 'running') return;
    const now = this.g.now;
    if (!Number.isFinite(now)) return;

    const hound = this.pick(entities);

    // This frame's depth, read before it is advanced: a growl that landed since
    // the last frame must be applied at its full depth on the frame it arrives,
    // not already a step into its own recovery.
    const dip = this.dipValue;
    this.advanceDip(dt);

    if (!hound) {
      this.standDown(now);
      return;
    }

    // First sight of a hound is what pays for the download. A player who never
    // meets one never fetches 622 KB.
    if (!this.ensure()) return;

    // A hound we have not sung for before starts the song from the top; one we
    // are merely picking back up after it walked out of earshot carries on
    // where it left off, which reads as a thing that was singing the whole time
    // and has just come back into range.
    if (hound.id !== this.targetId) {
      this.targetId = hound.id;
      this.rewind();
    }

    const s = evalSpatial(this.g.listener, hound.pos.x, hound.pos.z, this.scratch);
    const duck = active ? (MODE_DUCK[hound.mode] ?? MODE_DUCK_DEFAULT) : 0;
    const target = SONG_LEVEL * duck * dip * s.gain;

    // Beyond the audible limit `evalSpatial` has already reached exactly zero,
    // so stopping the element there costs nothing and saves decoding a track
    // nobody can hear.
    this.setPlaying(active && s.dist <= MAX_AUDIBLE + RANGE_SLACK);

    const tau = target < this.lastLevel ? TAU_DOWN : TAU_UP;
    this.lastLevel = target;
    this.level!.gain.setTargetAtTime(target, now, tau);
    this.panner!.pan.setTargetAtTime(s.pan, now, 0.06);
    this.lp!.frequency.setTargetAtTime(s.cutoff, now, 0.1);
    this.send!.gain.setTargetAtTime(clamp(SEND * s.sendScale, 0, 1.2), now, 0.08);
  }

  dispose(): void {
    this.targetId = null;
    this.stopElement();
    const el = this.el;
    this.el = null;
    this.src?.disconnect();
    this.hp?.disconnect();
    this.level?.disconnect();
    this.lp?.disconnect();
    this.panner?.disconnect();
    this.send?.disconnect();
    this.src = null;
    this.hp = null;
    this.level = null;
    this.lp = null;
    this.panner = null;
    this.send = null;
    if (!el) return;
    // Dropping the source lets the element release its decoded buffer now
    // rather than whenever it happens to be collected.
    try {
      el.removeAttribute('src');
      el.load();
    } catch {
      /* already torn down */
    }
  }

  /* ---------------------------------------------------------------- *
   * Internals
   * ---------------------------------------------------------------- */

  /**
   * Hold, then climb. Runs every frame whether or not anything is singing, so a
   * growl at the end of one encounter cannot leave the next one ducked.
   */
  private advanceDip(dt: number): void {
    if (this.dipHold > 0) this.dipHold = Math.max(0, this.dipHold - dt);
    else if (this.dipValue < 1) this.dipValue = clamp01(this.dipValue + dt / DIP_RECOVER);
  }

  /** The nearest living hound, with hysteresis so a pair cannot flip the source. */
  private pick(entities: readonly EntityState[]): EntityState | null {
    let best: EntityState | null = null;
    let current: EntityState | null = null;
    for (const e of entities) {
      if (e.kind !== 'hound' || e.mode === 'dead') continue;
      if (e.id === this.targetId) current = e;
      if (!best || e.distToPlayer < best.distToPlayer) best = e;
    }
    if (!best) return null;
    if (current && current !== best && current.distToPlayer - best.distToPlayer < RETARGET_MARGIN) {
      return current;
    }
    return best;
  }

  /** No hound: fade out and release the target, keeping the chain for the next one. */
  private standDown(now: number): void {
    this.targetId = null;
    if (!this.level) return;
    this.lastLevel = 0;
    this.level.gain.setTargetAtTime(0, now, TAU_DOWN);
    this.setPlaying(false);
  }

  /**
   * Build the element and the chain. Returns false forever once the platform
   * has refused, so a browser with no media element source path is a browser
   * where the hound simply does not sing.
   */
  private ensure(): boolean {
    if (this.level) return true;
    if (this.failed) return false;

    try {
      const ctx = this.g.ctx;
      const el = new Audio();
      el.src = SRC;
      // A hound can outlive the track. Looping means the song never just stops
      // on its own, which would be a false "it has gone" cue.
      el.loop = true;
      el.preload = 'auto';
      el.crossOrigin = 'anonymous';

      const src = ctx.createMediaElementSource(el);

      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = HIGHPASS_HZ;
      hp.Q.value = 0.707;

      const level = ctx.createGain();
      // Starts silent: a hound that spawns already inside the audible radius
      // should fade up, not arrive as a click.
      level.gain.value = 0;

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 20000;
      lp.Q.value = 0.4;

      const panner = ctx.createStereoPanner();
      const send = ctx.createGain();
      send.gain.value = 0;

      src.connect(hp);
      hp.connect(level);
      level.connect(lp);
      lp.connect(panner);
      // Both taps land upstream of `graph.master`, so master volume and mute
      // apply to the song for free and there is nothing to keep in sync.
      panner.connect(this.g.dry);
      panner.connect(send);
      send.connect(this.g.reverbIn);

      this.el = el;
      this.src = src;
      this.hp = hp;
      this.level = level;
      this.lp = lp;
      this.panner = panner;
      this.send = send;
      return true;
    } catch {
      // No media path on this platform. Nothing above has been reached from a
      // live graph node yet — the taps into `graph.dry` are the last thing to
      // happen — so the half-built chain is unreferenced and collectable, and
      // the hound simply hunts in silence, as it always did.
      this.failed = true;
      return false;
    }
  }

  private setPlaying(want: boolean): void {
    if (want === this.started || !this.el) return;
    this.started = want;
    if (want) {
      // Rejects if the file is missing or the autoplay policy is unhappy. The
      // context already needed a gesture to exist, so this is the missing-file
      // case, and there is nothing useful to do about it.
      void this.el.play().catch(() => undefined);
    } else {
      this.stopElement();
    }
  }

  private stopElement(): void {
    this.started = false;
    try {
      this.el?.pause();
    } catch {
      /* nothing to pause */
    }
  }

  private rewind(): void {
    try {
      if (this.el) this.el.currentTime = 0;
    } catch {
      // Seeking before metadata has arrived throws on some engines. It is
      // already at zero in that case, which is what we were asking for.
    }
  }
}
